import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import {
  STATUS_COLORS,
  SOURCE_COLORS,
  SOURCE_LABELS,
  formatDuration,
  formatCost,
} from "@/components/admin/ScraperShared";
import type { ScrapeJob, ToolCallLog } from "@/api/ecommerceScrapeService";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scrapeJob: ScrapeJob | null | undefined;
  loading: boolean;
  storeSlug?: string;
  storeId?: string;
};

export function ScraperRunDrawer({ open, onOpenChange, scrapeJob, loading, storeSlug, storeId }: Props) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto"
        dir="ltr"
      >
        <SheetHeader>
          <SheetTitle>Scraper Run</SheetTitle>
          <SheetDescription>
            How this draft was produced — sources, tool calls, and cost.
          </SheetDescription>
        </SheetHeader>

        <CopyAuditPromptButton storeSlug={storeSlug} storeId={storeId} jobId={scrapeJob?._id} />

        <div className="mt-4">
          {loading ? (
            <div className="animate-pulse bg-gray-100 h-24 rounded-lg" />
          ) : !scrapeJob ? (
            <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-500">
              No associated scraper run found. This store may have been created
              manually.
            </div>
          ) : (
            <ScraperRunBody scrapeJob={scrapeJob} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function CopyAuditPromptButton({
  storeSlug,
  storeId,
  jobId,
}: {
  storeSlug?: string;
  storeId?: string;
  jobId?: string;
}) {
  const [copied, setCopied] = useState(false);

  if (!storeSlug && !storeId && !jobId) return null;

  const buildPrompt = () => {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "https://yetfarrag.com";
    const lines = ["Audit this Yetfarrag store scrape using the evaluate-store-scrape skill:"];
    if (storeSlug) lines.push(`- Store URL: ${origin}/store/${storeSlug}`);
    if (storeId) lines.push(`- Store ID: ${storeId}`);
    if (jobId) lines.push(`- Job ID: ${jobId}`);
    return lines.join("\n");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildPrompt());
      setCopied(true);
      toast.success("Copied", { description: "Paste into Claude to run the audit." });
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed", { description: "Clipboard not available — copy manually from the prompt below." });
    }
  };

  return (
    <div className="mt-3">
      <Button size="sm" variant="outline" onClick={handleCopy} className="w-full gap-2">
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        {copied ? "Copied — paste into Claude" : "Copy audit prompt for Claude"}
      </Button>
    </div>
  );
}

function ScraperRunBody({ scrapeJob }: { scrapeJob: ScrapeJob }) {
  const totalCost =
    scrapeJob.toolCalls
      ?.filter((tc) => tc.tokenUsage)
      .reduce((sum, tc) => sum + (tc.tokenUsage?.costUsd || 0), 0) || 0;

  const agentToolCalls =
    scrapeJob.toolCalls?.filter((tc) => tc.toolName !== "_llm_call") || [];

  const enrichmentSteps =
    scrapeJob.toolCalls?.filter((tc) => tc.toolName.startsWith("enrich_")) || [];

  return (
    <div className="space-y-4">
      {/* Status row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[scrapeJob.status]}`}
        >
          {scrapeJob.status}
        </span>
        {scrapeJob.sourceType && (
          <span
            className={`px-1.5 py-0.5 rounded text-xs ${SOURCE_COLORS[scrapeJob.sourceType]}`}
          >
            {SOURCE_LABELS[scrapeJob.sourceType]}
          </span>
        )}
        <span className="text-xs text-gray-500 ml-auto">
          {new Date(scrapeJob.createdAt).toLocaleDateString()}
        </span>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-gray-50 rounded-lg p-2.5 text-center">
          <p className="text-[10px] text-gray-500 uppercase">Sources</p>
          <p className="text-lg font-bold">{scrapeJob.scrapedSources?.length || 0}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-2.5 text-center">
          <p className="text-[10px] text-gray-500 uppercase">Tools</p>
          <p className="text-lg font-bold">{agentToolCalls.length}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-2.5 text-center">
          <p className="text-[10px] text-gray-500 uppercase">Cost</p>
          <p className="text-lg font-bold">{formatCost(totalCost)}</p>
        </div>
      </div>

      {enrichmentSteps.length > 0 && (
        <div className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">
          {enrichmentSteps.length} enrichment step
          {enrichmentSteps.length > 1 ? "s" : ""} completed
        </div>
      )}

      {scrapeJob.error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
          {scrapeJob.error}
        </div>
      )}

      {scrapeJob.scrapedSources && scrapeJob.scrapedSources.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1.5">Scraped Sources</p>
          <div className="space-y-1">
            {scrapeJob.scrapedSources.map((src, i) => (
              <div
                key={i}
                className="flex items-center gap-2 bg-gray-50 rounded p-2 text-xs"
              >
                <span
                  className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${SOURCE_COLORS[src.platform]}`}
                >
                  {SOURCE_LABELS[src.platform]}
                </span>
                <a
                  href={src.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline truncate flex-1"
                >
                  {src.url.replace(/^https?:\/\/(www\.)?/, "").substring(0, 50)}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {agentToolCalls.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1.5">
            Tool Calls ({agentToolCalls.length})
          </p>
          <div className="space-y-1.5">
            {agentToolCalls.map((tc, i) => (
              <ToolCallRow key={i} toolCall={tc} index={i} />
            ))}
          </div>
        </div>
      )}

      <div className="text-xs pt-2 border-t">
        <span className="text-gray-400">Target:</span>{" "}
        <a
          href={scrapeJob.targetUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline break-all"
        >
          {scrapeJob.targetUrl}
        </a>
      </div>
    </div>
  );
}

function ToolCallRow({ toolCall, index }: { toolCall: ToolCallLog; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const success =
    toolCall.output && (toolCall.output as Record<string, unknown>).success !== false;
  const isEnrichment = toolCall.toolName.startsWith("enrich_");

  return (
    <div
      className={`border rounded text-xs ${success ? "border-gray-200" : "border-red-200"}`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-2.5 py-1.5 hover:bg-gray-50 text-left"
      >
        <div className="flex items-center gap-1.5">
          <span className="text-gray-400">#{index + 1}</span>
          <code className="font-mono font-medium text-[11px]">
            {toolCall.toolName}
          </code>
          {isEnrichment && (
            <span className="px-1 py-0.5 rounded text-[9px] font-medium bg-amber-100 text-amber-700">
              enrichment
            </span>
          )}
          <span
            className={`w-1.5 h-1.5 rounded-full ${success ? "bg-green-500" : "bg-red-500"}`}
          />
        </div>
        <div className="flex items-center gap-2 text-gray-500">
          <span>{formatDuration(toolCall.durationMs)}</span>
          {toolCall.tokenUsage && <span>{formatCost(toolCall.tokenUsage.costUsd)}</span>}
          <span
            className={`transform transition-transform ${expanded ? "rotate-90" : ""}`}
          >
            &#9654;
          </span>
        </div>
      </button>
      {expanded && (
        <div className="px-2.5 pb-2.5 space-y-1.5 border-t border-gray-100 pt-1.5">
          <div>
            <p className="text-[10px] text-gray-500 mb-0.5">Input</p>
            <pre className="text-[10px] bg-gray-50 p-1.5 rounded overflow-x-auto max-h-32">
              {JSON.stringify(toolCall.input, null, 2)}
            </pre>
          </div>
          {toolCall.output && (
            <div>
              <p className="text-[10px] text-gray-500 mb-0.5">Output</p>
              <pre className="text-[10px] bg-gray-50 p-1.5 rounded overflow-x-auto max-h-48">
                {JSON.stringify(toolCall.output, null, 2)}
              </pre>
            </div>
          )}
          {toolCall.error && (
            <p className="text-[10px] text-red-700 bg-red-50 p-1.5 rounded">
              {toolCall.error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
